import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { masterService } from "../../../api/masterService";
import { useNotification } from "../../../contexts/NotificationContext";
import SearchableSelect from "../../../components/SearchableSelect";

export default function EditInfoLainModal({ isOpen, onClose, product, onSuccess }) {
  const { showSuccess, showError } = useNotification();
  const [stockLocations, setStockLocations] = useState([]);
  const [formData, setFormData] = useState({
    supplier: product?.supplier || "",
    min_stock: product?.min_stock || 0,
    stock_location_id: product?.stock_location_id || "",
    rack_location: product?.rack_location || "",
    weight: product?.weight || "",
    dimensions: product?.dimensions || "",
    material: product?.material || "",
    warranty_months: product?.warranty_months || "",
    notes: product?.notes || product?.description || ""
  });
  
  const [tags, setTags] = useState(
    product?.tags
      ? (Array.isArray(product.tags) ? product.tags : product.tags.split(',')).map(t => t.trim())
      : []
  );
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && product) {
      let initialLocId = product.stock_location_id || product.stock_location?.id || "";

      setFormData({
        supplier: product.supplier || "",
        min_stock: product.min_stock || 0,
        stock_location_id: initialLocId,
        rack_location: product.rack_location || "",
        weight: product.weight || "",
        dimensions: product.dimensions || "",
        material: product.material || "",
        warranty_months: product.warranty_months || "",
        notes: product.notes || product.description || ""
      });

      if (product.tags) {
        setTags(Array.isArray(product.tags) ? product.tags : product.tags.split(',').map(t => t.trim()));
      } else {
        setTags([]);
      }

      masterService.stockLocations.list({ is_active: true })
        .then((res) => {
          const items = res?.data || res || [];
          const list = Array.isArray(items) ? items : [];
          const activeOnly = list.filter((loc) => loc.is_active !== false);
          setStockLocations(activeOnly);

          // Auto-match location ID by rack_location name if stock_location_id is empty
          if (!initialLocId && product.rack_location && activeOnly.length > 0) {
            const matched = activeOnly.find(l => l.name === product.rack_location);
            if (matched) {
              setFormData(prev => ({ ...prev, stock_location_id: matched.id }));
            }
          }
        })
        .catch((err) => console.error("Error fetching stock locations:", err));
    }
  }, [isOpen, product]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTagInput = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const newTag = tagInput.trim().replace(/,/g, '');
      if (newTag && !tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      setTagInput("");
    }
  };

  const removeTag = (indexToRemove) => {
    setTags(tags.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const tagsArray = tags.filter(t => t.trim() !== "");
      const selectedLoc = stockLocations.find(l => l.id === formData.stock_location_id);
      
      const payload = {
        name: product.name,
        description: product.description || null,
        purchase_price: product.purchase_price || 0,
        sell_price: product.sell_price || 0,
        category_id: product.category_id,
        unit_id: product.unit_id || null,
        sku: product.sku || null,
        barcode: product.barcode || null,
        status: product.status || "ACTIVE",
        is_track_stock: product.is_track_stock,
        ...formData,
        stock_location_id: formData.stock_location_id || null,
        min_stock: Number(formData.min_stock) || 0,
        weight: formData.weight ? Number(formData.weight) : null,
        warranty_months: formData.warranty_months ? Number(formData.warranty_months) : null,
        tags: tagsArray,
        supplier: formData.supplier || null,
        rack_location: selectedLoc ? selectedLoc.name : (formData.rack_location || null),
        dimensions: formData.dimensions || null,
        material: formData.material || null,
        notes: formData.notes || null,
      };

      await masterService.products.update(product.id, payload);
      showSuccess("Informasi lain berhasil diperbarui");
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      showError(error.response?.data?.message || error.message || "Gagal menyimpan informasi lain");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-100 animate-in fade-in zoom-in duration-200">
        <div className="px-6 py-4 flex justify-between items-center bg-teal-600 text-white rounded-t-2xl shrink-0">
          <h2 className="text-base font-bold tracking-tight">Edit Informasi Lain</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5 flex-1 text-sm text-slate-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Supplier */}
            <div className="space-y-1.5">
              <label className="block font-semibold text-slate-800">Supplier</label>
              <input 
                type="text" 
                name="supplier" 
                value={formData.supplier} 
                onChange={handleChange} 
                placeholder="Masukkan nama supplier"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-slate-800 placeholder:text-slate-400 text-sm transition" 
              />
            </div>

            {/* Minimum Stok */}
            <div className="space-y-1.5">
              <label className="block font-semibold text-slate-800">Minimum Stok</label>
              <input 
                type="number" 
                name="min_stock" 
                value={formData.min_stock} 
                onChange={handleChange} 
                placeholder="0"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-slate-800 placeholder:text-slate-400 text-sm transition" 
              />
            </div>

            {/* Lokasi Stok (SearchableSelect Component) */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="block font-semibold text-slate-800">Lokasi Stok</label>
              <SearchableSelect
                options={stockLocations}
                value={formData.stock_location_id}
                onChange={(val) => setFormData({ ...formData, stock_location_id: val })}
                placeholder="-- Pilih Lokasi Stok --"
                displayKey="name"
                valueKey="id"
                searchable={true}
              />
            </div>

            {/* Deskripsi (Sebelumnya Catatan) */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="block font-semibold text-slate-800">Deskripsi</label>
              <textarea 
                name="notes" 
                value={formData.notes} 
                onChange={handleChange} 
                rows="3" 
                placeholder="Masukkan deskripsi informasi lain"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-slate-800 placeholder:text-slate-400 text-sm transition resize-none" 
              />
            </div>

            {/* HIDDEN: Berat, Dimensi, Bahan, Garansi, Tag (Disembunyikan Sesuai Permintaan)
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Berat Produk (Gram)</label>
              <input type="number" name="weight" value={formData.weight} onChange={handleChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Dimensi</label>
              <input type="text" name="dimensions" value={formData.dimensions} onChange={handleChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Bahan</label>
              <input type="text" name="material" value={formData.material} onChange={handleChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-semibold text-slate-700">Garansi (Bulan)</label>
              <input type="number" name="warranty_months" value={formData.warranty_months} onChange={handleChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-sm font-semibold text-slate-700">Tag (Tekan Koma/Enter)</label>
              <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={handleTagInput} className="w-full px-4 py-2 border border-slate-200 rounded-xl" />
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((t, idx) => (
                  <span key={idx} className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs flex items-center gap-1">
                    {t}
                    <button type="button" onClick={() => removeTag(idx)} className="hover:text-teal-900">&times;</button>
                  </span>
                ))}
              </div>
            </div>
            */}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 shrink-0">
            <button type="button" onClick={onClose} className="px-5 py-2 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition text-sm">
              Batal
            </button>
            <button type="submit" disabled={loading} className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl shadow-sm transition text-sm">
              {loading ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
